/**
 * Site KPI Settings Tab (CR-5883)
 *
 * Restores the legacy SupplierPortal "Site KPI Settings" surface inside
 * Admin Setup → Performance. Each site the user can manage shows year-type
 * (Fiscal vs Calendar) and, for fiscal mode, the fiscal-year period.
 *
 * Phase 1 scope (CR-5883): expose the settings + persist them via the
 * existing SiteKPISettings model. Downstream wiring (dashboard year picker,
 * graph X-axis, YTD windows, supplier scorecards, analytics exports) lands
 * in subsequent phases.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Save, CalendarRange, Info } from 'lucide-react';
import { toast } from 'sonner';
import { performanceSettingsAPI, SiteKPISettings } from '../../../lib/api';
import { MONTH_OPTIONS } from './types';

type DraftMap = Record<number, { year_type: 'fiscal' | 'calendar'; fiscal_year_start_month: number }>;

function endMonthFromStart(startMonth: number): number {
  return startMonth === 1 ? 12 : startMonth - 1;
}

function startMonthFromEnd(endMonth: number): number {
  return endMonth === 12 ? 1 : endMonth + 1;
}

function periodLabel(startMonth: number): string {
  const start = MONTH_OPTIONS.find(m => m.value === startMonth)?.label ?? String(startMonth);
  const end = MONTH_OPTIONS.find(m => m.value === endMonthFromStart(startMonth))?.label ?? '';
  return `${start} → ${end}`;
}

export function SiteKPISettingsTab() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SiteKPISettings[]>([]);
  const [drafts, setDrafts] = useState<DraftMap>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await performanceSettingsAPI.getSiteKPISettingsForAccessibleSites();
        if (cancelled) return;
        const list = res.data ?? [];
        setRows(list);
        const initial: DraftMap = {};
        list.forEach(r => {
          initial[r.id] = {
            year_type: r.year_type,
            fiscal_year_start_month: startMonthFromEnd(r.fiscal_year_end_month),
          };
        });
        setDrafts(initial);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load Site KPI settings';
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (id: number, patch: Partial<DraftMap[number]>) => {
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const isDirty = (row: SiteKPISettings) => {
    const d = drafts[row.id];
    if (!d) return false;
    return (
      d.year_type !== row.year_type ||
      endMonthFromStart(d.fiscal_year_start_month) !== row.fiscal_year_end_month
    );
  };

  const handleSave = async (row: SiteKPISettings) => {
    const draft = drafts[row.id];
    if (!draft) return;
    setSavingId(row.id);
    try {
      const res = await performanceSettingsAPI.updateSiteKPISettings(row.id, {
        year_type: draft.year_type,
        fiscal_year_end_month: endMonthFromStart(draft.fiscal_year_start_month),
      });
      setRows(prev => prev.map(r => (r.id === row.id ? res.data : r)));
      setDrafts(prev => ({
        ...prev,
        [row.id]: {
          year_type: res.data.year_type,
          fiscal_year_start_month: startMonthFromEnd(res.data.fiscal_year_end_month),
        },
      }));
      toast.success(`Saved KPI settings for ${row.site_name}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Save failed';
      toast.error(message);
    } finally {
      setSavingId(null);
    }
  };

  const groupedRows = useMemo(() => {
    return rows.slice().sort((a, b) => a.site_name.localeCompare(b.site_name));
  }, [rows]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="ml-2 text-sm">Loading Site KPI settings…</span>
        </CardContent>
      </Card>
    );
  }

  if (groupedRows.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-sm text-muted-foreground flex items-center gap-2">
          <Info className="h-4 w-4" />
          No sites are accessible from this account. Site KPI settings appear once your user
          profile is linked to one or more sites.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" />
            <CardTitle>Site KPI Settings</CardTitle>
          </div>
          <CardDescription>
            Configure the reporting year per site. Fiscal mode lets dashboards and KPI summaries
            align with your fiscal calendar instead of the calendar year.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {groupedRows.map(row => {
            const draft = drafts[row.id] ?? {
              year_type: row.year_type,
              fiscal_year_start_month: startMonthFromEnd(row.fiscal_year_end_month),
            };
            const dirty = isDirty(row);
            const saving = savingId === row.id;
            return (
              <div
                key={row.id}
                className="grid grid-cols-1 md:grid-cols-[1fr_180px_220px_auto] gap-3 items-end border rounded-md p-3"
              >
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Site</Label>
                  <p className="text-sm font-medium">{row.site_name}</p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground" htmlFor={`year-type-${row.id}`}>
                    Year Type
                  </Label>
                  <Select
                    value={draft.year_type}
                    onValueChange={(v) => handleChange(row.id, { year_type: v as 'fiscal' | 'calendar' })}
                  >
                    <SelectTrigger id={`year-type-${row.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fiscal">Fiscal Year</SelectItem>
                      <SelectItem value="calendar">Calendar Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground" htmlFor={`fy-start-${row.id}`}>
                    Fiscal Year Period
                  </Label>
                  <Select
                    value={String(draft.fiscal_year_start_month)}
                    onValueChange={(v) => handleChange(row.id, { fiscal_year_start_month: Number(v) })}
                    disabled={draft.year_type === 'calendar'}
                  >
                    <SelectTrigger id={`fy-start-${row.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_OPTIONS.map(m => (
                        <SelectItem key={m.value} value={String(m.value)}>
                          {periodLabel(m.value)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  size="sm"
                  disabled={!dirty || saving}
                  onClick={() => handleSave(row)}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Saving
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground flex items-start gap-1.5 pt-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Calendar Year always runs January → December. For Fiscal Year, the period below sets
            the start and (implicit) end month — e.g. April → March is the UK fiscal year.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
